import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, Image, Link, StatusBanner, Text } from "@wayflyer/flyui";
import { RhfTextField } from "@wayflyer/flyui-hook-form";
import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { AuthCallbackPath } from "../../../api-types/connector-callbacks";
import { usePlatformConnectorProperties } from "../../../hooks/connector";
import type { WooCommerceData } from "../common/connect-by-post";
import { useConnectByPost } from "../common/connect-by-post";
import { ConnectionsList } from "../common/connections";
import { OAuthErrorBanner } from "../common/oauth-error-banner";
import { MESSAGES } from "../message-definitions";

const CONNECTOR_SLUG = "woocommerce2";
const WOOCOMMERCE_403_ERROR_KEY = "woocommerce 403 hint";

interface WooCommerceConnectProps {
  postConnectPath: AuthCallbackPath;
  onInitiateConnection?: () => void;
}

export const WooCommerceConnect = ({ postConnectPath, onInitiateConnection }: WooCommerceConnectProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(customerDomain, CONNECTOR_SLUG, postConnectPath);

  const connectByPost = useConnectByPost();
  const [showForbiddenBanner, setShowForbiddenBanner] = useState(false);

  // These hints also coming from connections themselves in customerweb.views.getting_started.ConnectWooCommerce
  const errors: Record<string, string> = {
    "valid woocommerce URL hint": intl.formatMessage({
      id: "funding-application: valid woocommerce URL hint",
      defaultMessage: "Please enter a valid WooCommerce store URL.",
    }),
    "valid woocommerce store hint": intl.formatMessage({
      id: "funding-application: valid woocommerce URL hint",
      defaultMessage: "Please enter a valid WooCommerce store URL.",
    }),
    [WOOCOMMERCE_403_ERROR_KEY]: intl.formatMessage({
      id: "funding-application: woocommerce 403 hint",
      defaultMessage: "We couldn't connect to your store. Your firewall may be blocking our access.",
    }),
  };

  const schema = Yup.object({
    storeName: Yup.string()
      .trim()
      .transform((value: string) => {
        if (value.startsWith("http://") || value.startsWith("https://")) {
          return value;
        } else {
          return "https://" + value;
        }
      })
      .url(errors["valid woocommerce URL hint"]),
  });

  const woocommerceForm = useForm<WooCommerceData>({
    resolver: yupResolver(schema),
  });

  const connectHandler = async (formData: WooCommerceData) => {
    if (connectorProperties.postPath) {
      onInitiateConnection?.();
      setShowForbiddenBanner(false);
      await connectByPost(connectorProperties.postPath, formData).then((response) => {
        if (response?.errorMessage) {
          if (response.errorMessage === WOOCOMMERCE_403_ERROR_KEY) {
            setShowForbiddenBanner(true);
          }
          if (response.errorField === "storeName") {
            woocommerceForm.clearErrors();
            woocommerceForm.setError("storeName", {
              type: "validate",
              message: Object.keys(errors).includes(response.errorMessage)
                ? errors[response.errorMessage]
                : errors["valid woocommerce URL hint"],
            });
          }
        }
      });
    }
  };

  return (
    <Flex direction="column" gap="8" align="flex-start" justify="space-between">
      <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
      {showForbiddenBanner && (
        <StatusBanner
          variant="warning"
          subheading={intl.formatMessage(
            {
              id: "funding-application: woocommerce 403 banner",
              defaultMessage:
                "We couldn't reach your store. Your firewall or security settings may be blocking our access. Please <link>configure your store to allow API calls from Wayflyer</link>.",
            },
            {
              link: (chunks: ReactNode) => (
                <Link
                  url="https://help-center.wayflyer.com/en/articles/268930-how-to-configure-your-self-hosted-store-to-allowlist-api-calls-from-wayflyer"
                  external
                >
                  {chunks}
                </Link>
              ),
            }
          )}
        />
      )}
      <Text>{intl.formatMessage(MESSAGES.ecommerceDataUsage, { platform: "WooCommerce" })}</Text>
      <ConnectionsList
        connectorProperties={connectorProperties}
        connectForm={
          <form onSubmit={woocommerceForm.handleSubmit(connectHandler)}>
            <RhfTextField
              label={intl.formatMessage({
                id: "funding-application: enter woocommerce store url",
                defaultMessage: "Enter store URL",
              })}
              labelIcon={connectorProperties.connectionLogoSrc && <Image src={connectorProperties.connectionLogoSrc} />}
              form={woocommerceForm}
              name="storeName"
              valuePrefix="https://"
              disabled={woocommerceForm.formState.isSubmitting}
              inlineButton={{
                label: "Log in",
                type: "submit",
                loading: woocommerceForm.formState.isSubmitting,
              }}
            />
          </form>
        }
      />
    </Flex>
  );
};
